/**
 * Rule Transformations
 * Converts between visual rule builder state and database TriageStrategy schema
 */

/**
 * Transform visual rule to database schema
 * @param {Object} visualRule - Visual rule builder state
 * @returns {Object} - Database-compatible rule structure
 */
export const visualRuleToDatabase = (visualRule) => {
  return {
    priority: visualRule.priority,
    name: visualRule.name,
    description: visualRule.description,
    isActive: visualRule.isActive !== false,
    conditions: visualRule.conditionBuilderState || visualRule.conditions,
    action: visualRule.actionBuilderState || visualRule.action
  };
};

/**
 * Transform database rule to visual state
 * @param {Object} dbRule - Database rule document
 * @returns {Object} - Visual builder state
 */
export const databaseRuleToVisual = (dbRule) => {
  return {
    priority: dbRule.priority,
    name: dbRule.name,
    description: dbRule.description,
    isActive: dbRule.isActive !== false,
    conditionBuilderState: dbRule.conditions,
    actionBuilderState: dbRule.action
  };
};

/**
 * Validate rule structure
 * @param {Object} rule - Rule to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateRuleStructure = (rule) => {
  const errors = [];

  if (!rule.name || rule.name.trim() === '') {
    errors.push('Rule name is required');
  }

  if (typeof rule.priority !== 'number' || rule.priority < 1) {
    errors.push('Priority must be a positive number');
  }

  // Validate conditions
  const conditionErrors = validateConditions(rule.conditions || rule.conditionBuilderState);
  errors.push(...conditionErrors);

  // Validate action
  const actionErrors = validateAction(rule.action || rule.actionBuilderState);
  errors.push(...actionErrors);

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate conditions structure (recursive)
 * @param {Object} conditions - Conditions object
 * @returns {string[]} - Array of error messages
 */
const validateConditions = (conditions) => {
  const errors = [];

  if (!conditions) {
    return ['Conditions are required'];
  }

  if (!['and', 'or', 'single'].includes(conditions.operator)) {
    errors.push('Invalid condition operator');
  }

  if (conditions.operator === 'single') {
    if (!conditions.criteria) {
      errors.push('Single condition requires criteria');
    } else {
      errors.push(...validateCriteria(conditions.criteria));
    }
  } else {
    if (!Array.isArray(conditions.conditions) || conditions.conditions.length === 0) {
      errors.push(`${conditions.operator.toUpperCase()} condition requires at least one sub-condition`);
    } else {
      conditions.conditions.forEach((subCondition, index) => {
        // Check if this is a nested condition group by looking for group operators (and/or/single)
        // NOT the comparison operators (==, !=, <, etc.) that criteria have
        const isConditionGroup = ['and', 'or', 'single'].includes(subCondition.operator);
        if (isConditionGroup) {
          // Nested condition group
          const nestedErrors = validateConditions(subCondition);
          errors.push(...nestedErrors.map(e => `Condition ${index + 1}: ${e}`));
        } else {
          // Criteria (has comparison operator like ==, !=, <, etc.)
          const criteriaErrors = validateCriteria(subCondition);
          errors.push(...criteriaErrors.map(e => `Condition ${index + 1}: ${e}`));
        }
      });
    }
  }

  return errors;
};

/**
 * Validate criteria
 * @param {Object} criteria - Criteria object
 * @returns {string[]} - Array of error messages
 */
const validateCriteria = (criteria) => {
  const errors = [];

  const validTypes = ['domain_score', 'total_score', 'flag_count', 'flag_present', 'assessment_band'];
  if (!validTypes.includes(criteria.type)) {
    errors.push('Invalid criteria type');
  }

  if (['domain_score', 'flag_present'].includes(criteria.type) && !criteria.domain) {
    errors.push('Domain is required for this criteria type');
  }

  if (criteria.operator === 'between' && (!Array.isArray(criteria.range) || criteria.range.length !== 2)) {
    errors.push('Between operator requires range array with 2 values');
  }

  if (['in', 'not_in'].includes(criteria.operator) && (!Array.isArray(criteria.values) || criteria.values.length === 0)) {
    errors.push('In/Not In operators require values array');
  }

  return errors;
};

/**
 * Validate action
 * @param {Object} action - Action object
 * @returns {string[]} - Array of error messages
 */
const validateAction = (action) => {
  const errors = [];

  if (!action) {
    return ['Action is required'];
  }

  const validActionTypes = ['assign_persona', 'human_review', 'escalate', 'alternative_pathway'];
  if (!validActionTypes.includes(action.type)) {
    errors.push('Invalid action type');
  }

  // Reason is always required by Mongoose schema
  if (!action.reason || action.reason.trim() === '') {
    errors.push('Reason/Message is required for all actions');
  }

  if (action.type === 'assign_persona' && !action.personaKey) {
    errors.push('Persona key is required for assign_persona action');
  }

  if (['human_review', 'escalate'].includes(action.type) && !action.assignToRole) {
    errors.push('Assign to role is required for this action type');
  }

  if (action.type === 'alternative_pathway' && typeof action.routeToStage !== 'number') {
    errors.push('Route to stage is required for alternative_pathway action');
  }

  return errors;
};

/**
 * Generate a human-readable description of a condition
 * @param {Object} conditions - Conditions object
 * @returns {string} - Human-readable description
 */
export const describeCondition = (conditions) => {
  if (!conditions) return 'No conditions';

  if (conditions.operator === 'single') {
    return describeCriteria(conditions.criteria);
  }

  // Handle case where conditions.conditions might be undefined or empty
  if (!conditions.conditions || !Array.isArray(conditions.conditions) || conditions.conditions.length === 0) {
    // Check if there's a criteria at top level (fallback)
    if (conditions.criteria) {
      return describeCriteria(conditions.criteria);
    }
    return 'No conditions defined';
  }

  const descriptions = conditions.conditions.map(c => {
    // Check if this is a nested condition group (has group operator like and/or/single)
    const isConditionGroup = ['and', 'or', 'single'].includes(c.operator);
    if (isConditionGroup) {
      return describeCondition(c);
    }
    // Otherwise it's flat criteria
    return describeCriteria(c);
  });

  return descriptions.join(` ${conditions.operator.toUpperCase()} `);
};

/**
 * Generate a human-readable description of criteria
 * @param {Object} criteria - Criteria object
 * @returns {string} - Human-readable description
 */
const describeCriteria = (criteria) => {
  if (!criteria) return 'Invalid criteria';

  const { type, domain, operator, value, range } = criteria;

  let description = '';

  switch (type) {
    case 'flag_present':
      description = `${domain} flag is present`;
      break;
    case 'domain_score':
      description = `${domain} score ${operator} ${value || range?.join('-')}`;
      break;
    case 'total_score':
      description = `Total score ${operator} ${value || range?.join('-')}`;
      break;
    case 'flag_count':
      description = `Flag count ${operator} ${value}`;
      break;
    case 'assessment_band':
      description = `Assessment band is ${value}`;
      break;
    default:
      description = 'Unknown condition';
  }

  return description;
};

/**
 * Generate a human-readable description of an action
 * @param {Object} action - Action object
 * @returns {string} - Human-readable description
 */
export const describeAction = (action) => {
  if (!action) return 'No action';

  switch (action.type) {
    case 'assign_persona':
      return `Assign ${action.personaKey} persona`;
    case 'human_review':
      return `Route to ${action.assignToRole} for review`;
    case 'escalate':
      return `Escalate to ${action.assignToRole}`;
    case 'alternative_pathway':
      return `Route to stage ${action.routeToStage}`;
    default:
      return 'Unknown action';
  }
};

/**
 * Sort rules by priority
 * @param {Array} rules - Array of rules
 * @returns {Array} - Sorted rules (lowest priority number first)
 */
export const sortRulesByPriority = (rules) => {
  return [...rules].sort((a, b) => a.priority - b.priority);
};

/**
 * Find next available priority
 * @param {Array} rules - Array of rules
 * @returns {number} - Next priority number
 */
export const getNextPriority = (rules) => {
  if (!rules || rules.length === 0) return 1;
  const maxPriority = Math.max(...rules.map(r => r.priority));
  return maxPriority + 1;
};
